import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { environment } from '@environment';
import { from, mergeMap, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TokenService } from './token.service';
import { NonprofitService } from '../../../nonprofit/nonprofit.service';
import { AuthService } from '../auth.service';

/**
 * Marker header callers can set to instruct the interceptor to omit the
 * active nonprofit id header for a single request. The interceptor strips
 * this header before the request leaves the client.
 */
export const SKIP_NONPROFIT_ID_HEADER = 'X-Skip-Nonprofit-Id';

/**
 * This interceptor is responsible for appending the access token to all outgoing requests to the API. It also appends the active nonprofit id to all requests unless the caller has opted out by setting the SKIP_NONPROFIT_ID_HEADER.
 */
@Injectable({ providedIn: 'root' })
export class TokenInterceptor implements HttpInterceptor {

  constructor (
    private tokenService: TokenService,
    private injector: Injector
  ) { }

  intercept (
    r: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return from((async (request) => {
      // we are trying to make an internal request if we omit http/https
      // i.e. /some/endpoint vs http://api.something.com/some/endpoint
      if (this.checkShouldProcessRequest(request.url)) {
        request = this.buildUpFullAPIURL(request);
        request = await this.appendToken(request);
      }

      return request;
    })(r))
    // if we got a request out of the previous observable, then we are good to go
    // otherwise stop the request
    .pipe(mergeMap((req) => {
      return next.handle(req).pipe(map(res => {
        if ('body' in res && res.body && (res.body.success === false)) {
          throw new HttpErrorResponse({
            error: res.body,
            url: req.url
          });
        }

        return res;
      }));
    }));
  }

  private async appendToken (request: HttpRequest<any>) {
    // Always strip the internal marker so it never leaves the client,
    // regardless of token/url branch.
    const skipRequested = request.headers.has(SKIP_NONPROFIT_ID_HEADER);
    if (skipRequested) {
      request = request.clone({
        headers: request.headers.delete(SKIP_NONPROFIT_ID_HEADER)
      });
    }

    if (this.checkShouldAppendToken(request.url)) {
      // capture whether or not we had a token before the attempted retrieval/refresh
      const latestToken = await this.tokenService.getLatestToken() as string;

      if (latestToken) {
        let headers = request.headers.set('Authorization', `Bearer ${latestToken}`);

        // Lazy inject to avoid circular dependency
        const nonprofitService = this.injector.get(NonprofitService);
        const authService = this.injector.get(AuthService);

        // A request may ask to opt out of nonprofit-scoped filtering via the
        // skip marker. For security we only honor that for users who are NOT
        // nonprofit admins/managers, so a nonprofit-scoped user can never drop
        // the nonprofit id and request data across other organizations.
        if (!skipRequested || authService.isNonprofitUser()) {
          const activeNpoId = nonprofitService.activeNpoId;
          if (activeNpoId) {
            headers = headers.set('nonprofitId', `${activeNpoId}`);
          }
        }

        request = request.clone({ headers });
      }
    }

    return request;
  }

  private checkShouldAppendToken (url: string) {
    return !url.startsWith(`${environment.apiBase}/api/admin/Sso`) && !url.startsWith(`${environment.apiBase}/api/Login`);
  }

  private buildUpFullAPIURL (request: HttpRequest<any>) {
    if (!request.url.startsWith('/')) {
      request = request.clone({
        url: '/' + request.url
      });
    }

    request = request.clone({
      url: `${environment.apiBase}${request.url}`
    });

    return request;
  }

  private checkShouldProcessRequest (url: string) {
    return !(/^(http|\/(InsertMissing|assets|Version|Log))/).test(url) &&
      (url.startsWith('/api') ||
      url.startsWith('api'));
  }
}
